export const categories = [
  {
    slug: 'historical-background',
    name: '历史背景',
    description: '探究《玉蒲团》诞生的时代背景与社会环境',
    icon: 'ri-history-line',
    color: 'accent',
  },
  {
    slug: 'literary-value',
    name: '文学价值',
    description: '深度解析《玉蒲团》的文学艺术成就与叙事技巧',
    icon: 'ri-book-open-line',
    color: 'primary',
  },
  {
    slug: 'version-research',
    name: '版本考证',
    description: '梳理《玉蒲团》历代版本流传与文本演变',
    icon: 'ri-file-search-line',
    color: 'secondary',
  },
  {
    slug: 'cultural-influence',
    name: '文化影响',
    description: '探讨《玉蒲团》对后世文学与文化的深远影响',
    icon: 'ri-global-line',
    color: 'accent',
  },
  {
    slug: 'character-analysis',
    name: '人物解析',
    description: '深入分析《玉蒲团》中主要人物的性格与命运',
    icon: 'ri-user-heart-line',
    color: 'secondary',
  },
  {
    slug: 'academic-research',
    name: '学术研究',
    description: '汇集学界关于《玉蒲团》的最新研究成果与论文',
    icon: 'ri-article-line',
    color: 'primary',
  },
];

export const articles = [
  {
    slug: 'ypt-historical-era-background',
    title: '《玉蒲团》成书年代与明清社会风貌探析',
    excerpt: '本文从明清之际的社会经济、文化思潮与市民生活入手，深入探讨《玉蒲团》诞生的历史条件，揭示作品背后复杂的时代语境。',
    category: 'historical-background',
    author: '文学研究院',
    date: '2024-12-15',
    readTime: '12分钟',
    imageSeq: 'article-bg-01',
    content: `
      <h2>引言</h2>
      <p>《玉蒲团》是中国古典文学史上一部极具争议又不可忽视的作品。它产生于明末清初这一特殊的历史时期，正值中国社会经历深刻变革之际。要真正理解这部作品，我们必须回到那个特定的历史语境中去。</p>
      
      <h2>明末清初的社会经济背景</h2>
      <p>明代中后期，商品经济蓬勃发展，江南地区出现了资本主义萌芽。苏州、杭州、南京等城市成为商业中心，市民阶层迅速壮大。这一新兴阶层有着不同于传统士大夫的文化需求——他们追求通俗易懂、贴近生活的文学作品。《玉蒲团》正是在这样的市场需求下应运而生。</p>
      <p>与此同时，印刷技术的进步使得书籍的传播范围大大扩展。明代万历年间，私人刻书业达到鼎盛，各类通俗读物大量印行。这为《玉蒲团》的广泛传播提供了技术条件。</p>
      
      <h2>文化思潮与思想解放</h2>
      <p>晚明时期，以李贽为代表的思想家提倡"童心说"，主张回归人的自然本性，反对程朱理学的束缚。这种思想解放潮流深刻影响了当时的文学创作。《玉蒲团》中对人性欲望的直接描绘，正是这一思潮在文学领域的具体体现。</p>
      <p>此外，王阳明心学的广泛传播也为人性的重新认识提供了哲学基础。"心即理"的观念使得人们开始关注个体的内在体验，文学创作也随之转向对个人情感和欲望的表达。</p>
      
      <h2>市民文学传统的延续</h2>
      <p>《玉蒲团》继承了宋元话本小说的叙事传统，同时又融合了明代章回小说的成熟技巧。作品中对市井生活的细腻描绘、对人物心理的深入刻画，都体现了当时小说艺术的高度发展。</p>
      
      <h2>结语</h2>
      <p>《玉蒲团》不是孤立产生的文学作品，它是时代经济、文化、思想多重因素交汇的结果。理解这部作品的历史背景，有助于我们更全面地认识明清之际中国文学的发展脉络。</p>
    `,
  },
  {
    slug: 'ypt-narrative-art-technique',
    title: '《玉蒲团》的叙事艺术与文学技巧研究',
    excerpt: '本文系统分析《玉蒲团》独特的叙事结构、章回体制运用以及修辞手法，揭示其在中国古典小说发展史上的独特地位。',
    category: 'literary-value',
    author: '文学研究院',
    date: '2025-01-20',
    readTime: '15分钟',
    imageSeq: 'article-bg-02',
    content: `
      <h2>引言</h2>
      <p>《玉蒲团》在叙事艺术上具有独特成就，其章回体制的运用、情节结构的安排以及人物形象的塑造，都展现了作者高超的文学造诣。本文化从叙事学视角对其进行系统分析。</p>
      
      <h2>章回体制的创新运用</h2>
      <p>《玉蒲团》采用回目形式构成完整叙事，每一回都有独立的故事情节和悬念设置。回末的"欲知后事如何，且听下回分解"不仅继承了话本小说的传统，更在叙事节奏控制上达到了新的高度。</p>
      <p>值得注意的是，作品在章回衔接上采用了"草蛇灰线，伏脉千里"的手法，前后情节相互照应，使整体结构紧凑而富有张力。</p>
      
      <h2>人物形象的多维塑造</h2>
      <p>作品中的人物并非简单的类型化形象，而是具有丰富层次感的立体人物。作者通过对话、心理描写和行动细节，多角度展现人物的性格特点和内心世界，这在同时期的通俗小说中相当难得。</p>
      
      <h2>语言风格与修辞特色</h2>
      <p>《玉蒲团》的语言兼具文言简练与白话生动的特点，在雅俗之间找到了恰当的平衡点。作者善用比喻、双关、对偶等修辞手法，使文本既富有文学性又不失可读性。</p>
      
      <h2>结语</h2>
      <p>《玉蒲团》的叙事艺术代表了明清通俗小说在技巧层面的成熟。对其进行深入研究，有助于我们更全面地理解中国古典小说叙事传统的发展演变。</p>
    `,
  },
  {
    slug: 'ypt-version-evolution-textual',
    title: '《玉蒲团》版本源流考：从抄本到刻本',
    excerpt: '本文详细考证《玉蒲团》从早期抄本到清代刻本的版本演变过程，比较各版本之间的文本差异，为学术研究提供可靠的版本依据。',
    category: 'version-research',
    author: '古籍研究所',
    date: '2025-02-08',
    readTime: '18分钟',
    imageSeq: 'article-bg-03',
    content: `
      <h2>引言</h2>
      <p>《玉蒲团》的版本问题一直是学界关注的焦点。由于作品在历史上长期处于地下传播状态，其版本系统极为复杂。本文试图通过系统梳理现存版本，还原其版本流传的基本脉络。</p>
      
      <h2>早期抄本系统</h2>
      <p>在刻本出现之前，《玉蒲团》主要以手抄本形式在民间流传。现存最早的抄本可追溯至清初，各抄本之间在文字、情节乃至回目数量上均存在差异。这些差异反映了作品在流传过程中不断被修改和扩充的过程。</p>
      
      <h2>清代刻本系统</h2>
      <p>清代中后期，随着印刷技术的发展，《玉蒲团》开始出现刻本。不同地区的刻书坊根据各自掌握的底本进行刊刻，形成了多个版本系统。其中以"本衙藏板本"和"啸花轩本"最为著名。</p>
      
      <h2>版本校勘与文本复原</h2>
      <p>通过对比各版本之间的异文，研究者可以逐步还原作品的原始面貌。近年来，随着数字化技术的应用，版本校勘工作取得了显著进展。</p>
      
      <h2>结语</h2>
      <p>《玉蒲团》的版本研究是一个需要持续深入的学术课题。对各版本的系统梳理，不仅是文献学的基础工作，也为文学研究提供了可靠的文本依据。</p>
    `,
  },
  {
    slug: 'ypt-cultural-legacy-influence',
    title: '《玉蒲团》对后世文学与文化的影响',
    excerpt: '本文从文学影响、文化符号、影视改编三个维度，探讨《玉蒲团》如何穿越时空界限，持续影响后世创作与社会文化。',
    category: 'cultural-influence',
    author: '文化研究中心',
    date: '2025-03-12',
    readTime: '14分钟',
    imageSeq: 'article-bg-04',
    content: `
      <h2>引言</h2>
      <p>《玉蒲团》的影响远远超出了文学领域。数百年来，它不仅影响了后续的文学创作，还渗透到民间文化、戏剧影视等多个领域，成为一个持续产生意义的文化符号。</p>
      
      <h2>文学传承与影响</h2>
      <p>在清代小说创作中，《玉蒲团》的叙事模式和主题处理方式被广泛借鉴。许多后续的通俗文学作品在情节设计和人物塑造上都能看到其影响。作品中的某些经典桥段甚至成为后世文学中反复出现的母题。</p>
      
      <h2>作为文化符号的演变</h2>
      <p>随着时代变迁，《玉蒲团》逐渐超越了单纯的文学作品范畴，成为一个具有多义性的文化符号。在不同的历史时期，人们对它的解读和评价不断变化，折射出社会观念的演变。</p>
      
      <h2>现代影视与数字时代的再创造</h2>
      <p>二十世纪以来，《玉蒲团》多次被改编为影视作品，以不同的媒介形式与新一代受众相遇。这些改编作品在保留原著核心精神的同时，也融入了现代审美和技术手段。</p>
      
      <h2>结语</h2>
      <p>《玉蒲团》的文化影响力证明了经典文学作品的生命力。它不断被重新解读和创造的过程，正是文化传统在时间长河中持续流动的生动例证。</p>
    `,
  },
  {
    slug: 'ypt-main-character-deep-analysis',
    title: '《玉蒲团》主要人物形象深度解析',
    excerpt: '本文对《玉蒲团》中核心人物的性格特征、行为逻辑及命运轨迹进行深度剖析，透过人物理解作品的人文关怀与时代精神。',
    category: 'character-analysis',
    author: '文学研究院',
    date: '2025-04-05',
    readTime: '16分钟',
    imageSeq: 'article-bg-05',
    content: `
      <h2>引言</h2>
      <p>人物是小说艺术的核心。《玉蒲团》塑造了一系列性格鲜明、命运各异的人物形象，通过这些人物，作者表达了对人性、欲望与道德的深刻思考。</p>
      
      <h2>未央生：欲望与救赎的辩证</h2>
      <p>作为全书的核心人物，未央生的形象塑造体现了作者对人性的复杂理解。他既是欲望的追逐者，又在经历种种波折后走向反思与救赎。这种从沉沦到觉醒的心路历程，赋予了作品以深刻的人文内涵。</p>
      
      <h2>女性群像的多维呈现</h2>
      <p>作品中的女性人物并非简单的欲望对象，而是各有性格和命运的独立个体。她们的行为选择和命运走向，反映了特定历史条件下女性的生存状态和自我意识。</p>
      
      <h2>配角的功能与象征</h2>
      <p>作品中的配角人物各具功能：有的推动情节发展，有的作为主角的对照，有的承载特定的道德寓意。这些人物的精心安排使作品的整体结构更加丰富和完整。</p>
      
      <h2>结语</h2>
      <p>《玉蒲团》的人物塑造在明清通俗小说中独树一帜。通过对这些人物的深入分析，我们能够更深刻地理解作品的艺术成就和思想内涵。</p>
    `,
  },
  {
    slug: 'ypt-academic-research-review',
    title: '近三十年《玉蒲团》学术研究综述',
    excerpt: '本文系统梳理近三十年来国内外学界关于《玉蒲团》的研究成果，涵盖文献学、文学批评、文化研究等多个领域。',
    category: 'academic-research',
    author: '学术编辑部',
    date: '2025-05-18',
    readTime: '20分钟',
    imageSeq: 'article-bg-06',
    content: `
      <h2>引言</h2>
      <p>近三十年来，随着学术环境的日益开放和研究方法的多元化，《玉蒲团》研究取得了丰硕成果。本文旨在对这些研究成果进行系统梳理和评述。</p>
      
      <h2>文献学研究成果</h2>
      <p>在文献学领域，学者们对《玉蒲团》的版本系统进行了系统梳理，发现了若干重要版本，并对作者问题提出了新的见解。版本数字化工作也为研究者提供了更加便捷的文本比较工具。</p>
      
      <h2>文学批评视角的拓展</h2>
      <p>从叙事学、女性主义批评到文化研究，多元化的理论视角为《玉蒲团》研究注入了新的活力。学者们不再局限于道德评判，而是更加关注作品的艺术成就和文化意义。</p>
      
      <h2>国际学术界的关注</h2>
      <p>近年来，海外汉学界对《玉蒲团》的关注度持续上升。英文、日文、法文等多种语言的研究成果不断涌现，推动了该领域的国际化交流。</p>
      
      <h2>未来研究展望</h2>
      <p>展望未来，《玉蒲团》研究仍有广阔的拓展空间。跨学科研究方法的引入、数字人文技术的应用，都将为这一领域带来新的可能性。</p>
      
      <h2>结语</h2>
      <p>《玉蒲团》学术研究的蓬勃发展，反映了学界对其文学价值和文化意义的日益重视。期待未来有更多高质量的研究成果涌现。</p>
    `,
  },
  {
    slug: 'ypt-ming-qing-literature-context',
    title: '《玉蒲团》与明清通俗小说发展脉络',
    excerpt: '本文将《玉蒲团》置于明清通俗小说发展的宏观脉络中，比较其与同期作品的异同，揭示其在文学史上的独特坐标。',
    category: 'literary-value',
    author: '文学研究院',
    date: '2025-06-22',
    readTime: '13分钟',
    imageSeq: 'article-bg-07',
    content: `
      <h2>引言</h2>
      <p>要准确评价《玉蒲团》的文学史地位，必须将其置于明清通俗小说发展的整体脉络中进行考察。通过横向与纵向的对比分析，我们能够更清晰地认识其独特性。</p>
      
      <h2>明代通俗小说的繁荣</h2>
      <p>明代是中国通俗小说发展的黄金时期。《三国演义》《水浒传》《西游记》《金瓶梅》等"四大奇书"的出现，标志着长篇小说艺术的成熟。《玉蒲团》正是在这一繁荣背景下产生的。</p>
      
      <h2>与同期作品的比较</h2>
      <p>与《金瓶梅》等同期作品相比，《玉蒲团》在叙事技巧和主题处理上既有继承又有创新。其独特的叙事视角和结构安排，使其在同类作品中别具一格。</p>
      
      <h2>对清代小说的影响</h2>
      <p>《玉蒲团》对清代小说创作产生了深远影响。《红楼梦》《儒林外史》等经典作品中的某些叙事技巧和主题元素，都可以追溯到《玉蒲团》的传统。</p>
      
      <h2>结语</h2>
      <p>《玉蒲团》在明清通俗小说发展史上占有不可忽视的地位。对其进行深入研究，有助于完善我们对中国古典小说发展全貌的认识。</p>
    `,
  },
  {
    slug: 'ypt-social-customs-ming-qing',
    title: '从《玉蒲团》看明清社会的市井风情',
    excerpt: '本文以《玉蒲团》中的市井生活描写为窗口，还原明清时期的社会风俗、商业活动和市民文化面貌。',
    category: 'historical-background',
    author: '历史文化所',
    date: '2025-07-10',
    readTime: '11分钟',
    imageSeq: 'article-bg-08',
    content: `
      <h2>引言</h2>
      <p>《玉蒲团》不仅是一部文学作品，更是一面反映明清社会生活的镜子。作品中对市井风情的细腻描绘，为后世研究明清社会史提供了珍贵的文本资料。</p>
      
      <h2>商业活动与城市生活</h2>
      <p>作品中多次描绘了明清城市的商业场景：繁华的街市、热闹的茶馆、穿梭的商贩……这些细节生动还原了当时商品经济活跃的城市面貌。</p>
      
      <h2>市民阶层的日常生活</h2>
      <p>通过对作品中人物日常生活的描写，我们可以了解明清市民阶层的衣食住行、婚丧嫁娶、休闲娱乐等方方面面。这些内容具有重要的社会史价值。</p>
      
      <h2>民间信仰与风俗习惯</h2>
      <p>作品中还涉及了大量民间信仰和风俗习惯的描写，如节日庆典、宗教活动、民间禁忌等，为研究明清民间文化提供了丰富的素材。</p>
      
      <h2>结语</h2>
      <p>《玉蒲团》作为一部扎根于社会现实的作品，其对社会生活的忠实记录使其具有超越文学的多元价值。</p>
    `,
  },
  {
    slug: 'ypt-translation-overseas-spread',
    title: '《玉蒲团》的海外译介与国际传播',
    excerpt: '本文梳理《玉蒲团》在不同国家和地区的翻译出版历程，分析其在跨文化传播中的接受情况与文化适应策略。',
    category: 'cultural-influence',
    author: '文化研究中心',
    date: '2025-08-03',
    readTime: '12分钟',
    imageSeq: 'article-bg-09',
    content: `
      <h2>引言</h2>
      <p>《玉蒲团》是最早被译介到西方的中国古典小说之一。其在海外的传播历程，是研究中国文学跨文化交流的重要案例。</p>
      
      <h2>早期西文译本</h2>
      <p>19世纪末至20世纪初，《玉蒲团》开始出现英文、法文等西文译本。这些早期译本在翻译策略上各有特点，有的注重忠实原著，有的则进行了大幅度的改编以适应西方读者的阅读习惯。</p>
      
      <h2>日本及东亚地区的传播</h2>
      <p>在汉字文化圈内，《玉蒲团》的传播更为便捷。日本江户时代即有和刻本流传，对日本通俗文学产生了一定影响。</p>
      
      <h2>当代译本的多元化</h2>
      <p>进入21世纪，随着中国文学"走出去"战略的推进，《玉蒲团》的新译本不断涌现。翻译质量的提升和学术导读的加入，使海外读者能够更准确地理解这部作品。</p>
      
      <h2>结语</h2>
      <p>《玉蒲团》的海外传播历程表明，优秀的文学作品具有跨越文化和时空界限的生命力。</p>
    `,
  },
];

export const faqs = [
  {
    question: '《玉蒲团》的作者是谁？历史上是否确有其人？',
    answer: '关于《玉蒲团》的作者，学界尚无定论。传统说法多归于李渔，但此说缺乏确凿证据。作者真实身份仍是学术研究中的重要课题。目前学者倾向于认为作者应是明末清初一位具有较高文化修养的文人，熟悉市井生活且精通小说创作技巧。',
  },
  {
    question: '《玉蒲团》的成书时间是什么时候？',
    answer: '学界普遍认为《玉蒲团》成书于明末清初，大致在17世纪中叶。作品中反映的社会生活、语言风格以及印刷传播方式，都指向这一时期。不过由于早期版本的缺失，确切成书年代仍有争议。',
  },
  {
    question: '《玉蒲团》在文学史上有什么样的地位？',
    answer: '《玉蒲团》被视为明清通俗小说的重要代表作品之一。其在叙事技巧、人物塑造和主题深度方面都有独到成就，对后世文学创作产生了深远影响。虽然长期处于地下传播状态，但其文学价值已得到学界的广泛认可。',
  },
  {
    question: '《玉蒲团》现存有哪些主要版本？',
    answer: '《玉蒲团》现存版本系统复杂，主要包括早期抄本和清代刻本两大系统。其中刻本以"本衙藏板本"和"啸花轩本"最为著名。各版本在回目数量、文字内容上存在差异，版本校勘是学术界持续关注的课题。',
  },
  {
    question: '如何正确理解《玉蒲团》的文化价值？',
    answer: '《玉蒲团》的价值不仅在于其文学成就，更在于它反映了特定历史时期的社会风貌、价值观念和人性思考。应当将其置于明清社会文化的大背景下，从文学、历史、社会学等多维度进行综合理解，而非以单一的道德标准进行评判。',
  },
  {
    question: '《玉蒲团》与《金瓶梅》有什么关系？',
    answer: '《玉蒲团》和《金瓶梅》都是明末清初的通俗小说代表作，两者在社会写实、人性描写方面有相似之处。《金瓶梅》对《玉蒲团》的创作有直接影响，但《玉蒲团》在叙事结构和主题处理上有自身的独特性。两部作品的比较研究是学术界的经典课题。',
  },
];

export const aboutInfo = {
  brandName: '经典文学研究院',
  introduction: '经典文学研究院成立于2010年，是一家专注于中国古典文学研究与推广的学术机构。我们汇聚了来自全国各地的古典文学研究者，致力于深度挖掘、系统整理和广泛传播中国古典文学的精髓与智慧。',
  mission: '以严谨的学术态度和开放的文化视野，架设古典文学与当代读者之间的桥梁，让经典作品焕发新的生命力。',
  honors: [
    { year: '2023', title: '获评"全国优秀文化传播机构"' },
    { year: '2022', title: '荣获"中华优秀传统文化传承奖"' },
    { year: '2021', title: '出版《明清小说研究丛书》（全十二卷）获国家级学术著作奖' },
    { year: '2020', title: '与北京大学中文系联合成立"明清文学研究中心"' },
    { year: '2019', title: '发起"中国古典文学数字典藏计划"，累计数字化古籍3000余册' },
  ],
  contact: {
    address: '北京市海淀区中关村南大街5号 文化大厦B座12层',
    phone: '010-8888-6666',
    email: 'contact@epyuanchang.cn',
    wechat: 'jingdianwenxue',
  },
};